import type { TranslateNS } from '@deepseek-ai/dsh-client-ui-slots'

export const ARKME_LOGIN_LOCALE_NAMESPACE = 'arkme.login'

export const arkmeLoginZh = {
  'locale.id': 'zh',
  'story.aria': '即我产品定义',
  'story.title.first': '即我，',
  'story.title.second': '你的数字自我',
  'story.description': '连接你的经历，成为更懂你的数字自我。',
  'brand.alt': '即我',
  'account.setup': '完成账号设置',
  welcome: '欢迎回来',
  'title.binding': '完成登录',
  'title.login': '登录即我',
  'subtitle.binding': '验证手机号后即可继续',
  'binding.notice': '当前即我账号还没有绑定手机号，请先完成手机号验证，完成后才会登录成功。',
  'tabs.aria': '登录方式',
  'tab.jiwo': '即我扫码',
  'tab.wechat': '微信扫码',
  'tab.phone': '手机号登录',
  'tab.test': '测试账号',
  'qr.title': '请使用微信扫码登录',
  'qr.jiwo.title': '请使用即我 App 扫码登录',
  'guide.label': '扫码指引',
  'guide.swipe': '向右滑动',
  'guide.plus': '点击右上角“＋”',
  'guide.choose': '点击“扫一扫”',
  'guide.update': '未找到“扫一扫”？请更新至最新版',
  'guide.app': '即我',
  'guide.today': '今天',
  'guide.chats': '对话列表',
  'guide.self': '发给自己',
  'guide.contact': '添加联系人',
  'guide.group': '创建群聊',
  'guide.bot': '添加 Bot',
  'guide.scan': '扫一扫',
  'qr.loading': '二维码加载中',
  'qr.relogin': '重新登录',
  'qr.alt': '微信扫码登录即我',
  'qr.jiwo.alt': '即我 App 扫码登录即我',
  'qr.refresh.aria': '刷新微信登录二维码',
  'qr.refreshing.aria': '正在刷新微信登录二维码',
  'qr.jiwo.refresh.aria': '刷新即我 App 登录二维码',
  'qr.jiwo.refreshing.aria': '正在刷新即我 App 登录二维码',
  'qr.refresh': '↻ 点击刷新',
  'qr.refreshing': '正在刷新…',
  'phone.label': '手机号',
  'phone.placeholder': '请输入 11 位手机号',
  'phone.aria': '手机号',
  'code.label': '验证码',
  'code.placeholder': '请输入 6 位验证码',
  'code.aria': '短信验证码',
  'code.get': '获取验证码',
  'binding.submitting': '正在绑定…',
  'binding.complete': '完成绑定',
  'binding.cancel': '取消绑定',
  'login.submitting': '正在登录…',
  'login.submit': '登录',
  'test.note': '测试环境可使用测试用户编号直接登录，登录后仍会检查手机号绑定状态。',
  'test.label': '测试用户编号',
  'test.placeholder': '请输入测试用户编号',
  'test.aria': '测试用户编号',
  'test.submit': '测试账号登录',
  'agreement.warning': '请阅读并同意用户协议和隐私条款',
  'agreement.prefix': '我已阅读并同意',
  'agreement.user': '《用户协议》',
  'agreement.separator': '、',
  'agreement.privacy': '《隐私条款》',
  'gate.dialog': '即我登录',
  'gate.checking': '正在确认登录状态…',
  'gate.checking.arkme': '正在确认即我登录状态…',
  'gate.error.title': '暂时无法确认登录状态',
  'gate.go.login': '前往登录',
  'gate.retrying': '正在重试…',
  'gate.retry': '重新检查',
  'error.binding.required': '请先绑定手机号，再继续使用即我',
  'error.agreement.required': '请阅读并同意用户协议和隐私条款',
  'error.phone.eleven': '请输入正确的 11 位手机号',
  'error.phone.invalid': '请输入正确的手机号',
  'error.code.invalid': '请输入验证码',
  'error.test.invalid': '请输入有效的测试用户编号',
  'error.generic': '登录失败，请稍后重试',
  'error.wechat.unavailable': '当前无法获取微信二维码，请使用手机号登录',
  'error.qr.expired': '登录二维码已失效，请重新获取',
  'error.test.disabled': '测试账号登录仅允许在测试环境使用',
  'error.phone.bound': '该手机号已绑定其他即我账号',
  'error.phone.code': '手机号或验证码错误',
  'error.captcha.required': '请先完成安全验证',
  'error.login.required': '登录已失效，请重新登录',
  'error.account.unavailable': '当前即我账号暂不可用，如有疑问请联系工作人员',
} as const

export type ArkmeLoginLocaleKey = keyof typeof arkmeLoginZh

export const arkmeLoginEn: Record<ArkmeLoginLocaleKey, string> = {
  'locale.id': 'en',
  'story.aria': 'About Arkme',
  'story.title.first': 'Arkme,',
  'story.title.second': 'Digital ark, true me',
  'story.description': 'Your experiences, connected into a digital you.',
  'brand.alt': 'Arkme',
  'account.setup': 'Finish account setup',
  welcome: 'Welcome back',
  'title.binding': 'Finish signing in',
  'title.login': 'Sign in to Arkme',
  'subtitle.binding': 'Verify your phone number to continue',
  'binding.notice': 'This Arkme account does not have a phone number yet. Verify one to finish signing in.',
  'tabs.aria': 'Sign-in methods',
  'tab.jiwo': 'Arkme QR',
  'tab.wechat': 'Wechat QR',
  'tab.phone': 'Phone number',
  'tab.test': 'Test account',
  'qr.title': 'Scan with WeChat to sign in',
  'qr.jiwo.title': 'Scan with the Arkme app to sign in',
  'guide.label': 'Scan guide',
  'guide.swipe': 'Swipe right',
  'guide.plus': 'Tap “+” at the top right',
  'guide.choose': 'Tap “Scan”',
  'guide.update': 'No Scan option? Update to the latest app.',
  'guide.app': 'Arkme',
  'guide.today': 'Today',
  'guide.chats': 'Chats',
  'guide.self': 'Notes to self',
  'guide.contact': 'Add contact',
  'guide.group': 'Create group',
  'guide.bot': 'Add Bot',
  'guide.scan': 'Scan',
  'qr.loading': 'Loading QR code',
  'qr.relogin': 'Try again',
  'qr.alt': 'WeChat QR code for signing in to Arkme',
  'qr.jiwo.alt': 'Arkme app QR code for signing in to Arkme',
  'qr.refresh.aria': 'Refresh WeChat sign-in QR code',
  'qr.refreshing.aria': 'Refreshing WeChat sign-in QR code',
  'qr.jiwo.refresh.aria': 'Refresh Arkme app sign-in QR code',
  'qr.jiwo.refreshing.aria': 'Refreshing Arkme app sign-in QR code',
  'qr.refresh': '↻ Click to refresh',
  'qr.refreshing': 'Refreshing…',
  'phone.label': 'Phone number',
  'phone.placeholder': 'Enter an 11-digit phone number',
  'phone.aria': 'Phone number',
  'code.label': 'Verification code',
  'code.placeholder': 'Enter the 6-digit code',
  'code.aria': 'SMS verification code',
  'code.get': 'Send code',
  'binding.submitting': 'Binding…',
  'binding.complete': 'Finish binding',
  'binding.cancel': 'Cancel',
  'login.submitting': 'Signing in…',
  'login.submit': 'Sign in',
  'test.note': 'In the test environment, you can sign in with a test user ID. Phone binding is still checked after sign-in.',
  'test.label': 'Test user ID',
  'test.placeholder': 'Enter a test user ID',
  'test.aria': 'Test user ID',
  'test.submit': 'Sign in with test account',
  'agreement.warning': 'Please read and accept the User Agreement and Privacy Policy',
  'agreement.prefix': 'I have read and accept the',
  'agreement.user': 'User Agreement',
  'agreement.separator': ' and ',
  'agreement.privacy': 'Privacy Policy',
  'gate.dialog': 'Arkme sign-in',
  'gate.checking': 'Checking sign-in status…',
  'gate.checking.arkme': 'Checking Arkme sign-in status…',
  'gate.error.title': 'Unable to check sign-in status',
  'gate.go.login': 'Go to sign in',
  'gate.retrying': 'Retrying…',
  'gate.retry': 'Check again',
  'error.binding.required': 'Bind a phone number before continuing with Arkme',
  'error.agreement.required': 'Please read and accept the User Agreement and Privacy Policy',
  'error.phone.eleven': 'Enter a valid 11-digit phone number',
  'error.phone.invalid': 'Enter a valid phone number',
  'error.code.invalid': 'Enter the verification code',
  'error.test.invalid': 'Enter a valid test user ID',
  'error.generic': 'Sign-in failed. Please try again later.',
  'error.wechat.unavailable': 'A WeChat QR code is unavailable. Sign in with a phone number instead.',
  'error.qr.expired': 'The sign-in QR code has expired. Request a new one.',
  'error.test.disabled': 'Test account sign-in is available only in the test environment.',
  'error.phone.bound': 'This phone number is already bound to another Arkme account.',
  'error.phone.code': 'The phone number or verification code is incorrect.',
  'error.captcha.required': 'Complete the security verification first.',
  'error.login.required': 'Your sign-in has expired. Sign in again.',
  'error.account.unavailable': 'This Arkme account is currently unavailable. Contact support if you need help.',
}

declare module '@deepseek-ai/dsh-client-ui-slots' {
  interface LocaleNamespaceMap {
    'arkme.login': ArkmeLoginLocaleKey
  }
}

export type ArkmeLoginTranslate = TranslateNS<typeof ARKME_LOGIN_LOCALE_NAMESPACE>

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (params === undefined) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (
    name in params ? String(params[name]) : match
  ))
}

export const defaultArkmeLoginTranslate: ArkmeLoginTranslate = ((key: ArkmeLoginLocaleKey, params?: Record<string, string | number>) => (
  interpolate(arkmeLoginZh[key], params)
)) as ArkmeLoginTranslate
